import {
	ReactElement,
	useMemo,
	useState
} from "react";
import { SettingsPage } from "../SettingsPage";
import {
	TableView,
	SearchInput,
	Pagination,
	SearchPlaceholder,
	TableViewNoResultConfig
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	createLocalDataSource,
	useSelection,
	useSortHandler
} from "client/Hooks";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import { contains } from "client/Data/searching";
import { ConnectionModel } from "client/Api/ClientlessRdp/clientlessRdp";
import { resolveFormattedTextString } from "client/Misc/utils";

const {
	Row,
	Cell
} = TableView;

export type ConnectionsSectionProps = {
	failedToLoadConnections: boolean;
	connections: readonly ConnectionModel[];
	selection: readonly ConnectionModel[];
	onSelectionChange(selection: readonly ConnectionModel[]): void;
};

const useConnectionData = createLocalDataSource<ConnectionModel>()
	.setConfiguration({
		search: {
			doesMatchItem: searchMatchFunction,
		},
		hasPagination: true,
	})
	.getHook();

export const ConnectionsSection = (props: ConnectionsSectionProps): ReactElement => {
	const {
		failedToLoadConnections,
		connections,
		selection,
		onSelectionChange
	} = props;

	const [sortingState, setSortingState] = useState<DataSourceSortableState<ConnectionModel>>(
		{ propertyName: "name", direction: DataSourceSortDirection.Ascending }
	);
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [searchTerm, setSearchTerm] = useState<string>("");

	const { sortingFor } = useSortHandler<ConnectionModel>(sortingState, setSortingState);

	const { currentItems, pagination } = useConnectionData(connections, {
		sorting: sortingState,
		pagination: {
			currentPageNumber,
		},
		searchTerm,
	});

	const { multiSelection, selectionFor } = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems: selection,
			setSelectedItems: onSelectionChange,
			hasMemorization: true,
			allItems: connections
		}
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (failedToLoadConnections) {
				return {
					icon: "gear",
					title: localize("_FailedToRetrieveConnections"),
					message: localize("_PleaseCheckTheGatewaySettings")
				};
			}

			if (searchTerm) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "gear",
				title: resolveFormattedTextString({
					template: localize("_NoXIntegrationConnectionsYet"),
					replacements: [
						{
							what: "{{IntegrationName}}",
							filler: localize("_ClientlessRDP")
						}
					]
				}),
				message: resolveFormattedTextString({
					template: localize("_ThereAreNoXIntegrationConnectionsYet"),
					replacements: [
						{
							what: "{{IntegrationName}}",
							filler: localize("_ClientlessRDP")
						}
					]
				})
			};
		},
		[searchTerm, failedToLoadConnections]
	);

	return (
		<SettingsPage.Section
			title={localize("_Connections")}
			height="stretch"
		>
			<SettingsPage.Section
				height="stretch"
			>
				<SettingsPage.SearchSection>
					<SearchInput
						onChange={setSearchTerm}
						icon="filter"
						height="large"
						placeholder={(
							<SearchPlaceholder
								kind="filter"
								properties={[
									localize("_Name"),
									localize("_ServerAddress")
								]}
							/>
						)}
					/>
				</SettingsPage.SearchSection>

				<SettingsPage.Section height="stretch">
					<TableView
						multiSelectable={multiSelection}
						items={currentItems}
						itemKey="id"
						height="stretch"
						columns={[
							{
								title: localize("_Name"),
								sortable: sortingFor("name"),
								trackingId: "name"
							},
							{
								title: localize("_ServerAddress"),
								sortable: sortingFor("serverAddress"),
								trackingId: "server-address"
							}
						]}
						trackingId="clientless-rdp-connection-settings"
						noResultConfig={noResultConfig}
					>
						{item => (
							<Row
								selectable={selectionFor(item)}
								trackingId={item.id!}
							>
								<Cell>{item.name}</Cell>
								<Cell>{item.serverAddress}</Cell>
							</Row>
						)}
					</TableView>
					<Pagination {...pagination} onPageNumberChanged={setCurrentPageNumber} />
				</SettingsPage.Section>
			</SettingsPage.Section>
		</SettingsPage.Section>
	);
};
ConnectionsSection.displayName = "ConnectionsSection";

function searchMatchFunction(
	item: ConnectionModel,
	normalizedNotEmptySearchTerm: Exclude<string, "">
): boolean {
	const {
		name,
		serverAddress
	} = item;

	return contains(name, normalizedNotEmptySearchTerm)
		|| contains(serverAddress, normalizedNotEmptySearchTerm);
}
